import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
type PreferenceCard = {
  id: string;
  titleKey: string;
  emoji: string;
  items?: string[];
  text?: string;
};
export function FornitoreHomePage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  // Separate products data
  const productsData = {
    preferred: {
      id: 'preferred',
      titleKey: 'preferredProducts',
      emoji: '✅',
      items: ['Detergente Bio', 'Anticalcare X', 'Sgrassatore Limone']
    },
    avoid: {
      id: 'avoid',
      titleKey: 'avoidProducts',
      emoji: '❌',
      items: ['Candeggina', 'Ammoniaca']
    }
  };
  // Special preferences and delicate materials
  const specialAndDelicate = {
    special: {
      id: 'special',
      titleKey: 'specialPreferences',
      emoji: '⭐',
      items: ['Lenzuola sempre stirate', 'Cuscini divano sistemati in modo specifico', 'Lasciare luci ingresso accese']
    },
    delicate: {
      id: 'delicate',
      titleKey: 'delicateMaterials',
      emoji: '⚠️',
      items: ['Parquet antico', 'Marmo Carrara', 'Tessuti delicati']
    }
  };
  // Notes
  const notes = {
    id: 'notes',
    titleKey: 'additionalNotes',
    emoji: '📝',
    text: 'Il parquet in salotto è molto delicato, usare solo prodotti specifici e panno ben strizzato.'
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-799">
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 pb-32" data-id="element-800">
        <div className="mb-12" data-id="element-801">
          <h1 className="text-4xl font-bold text-navy mb-2" data-id="element-802">
            {t.fornitore.dashboard.title}
          </h1>
        </div>

        {/* Bento Box Grid - 2x2 with generous spacing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-803">
          {/* Prodotti Preferiti - Top Left */}
          <Card className="p-8" data-id="element-804">
            <div className="flex items-center gap-3 mb-5" data-id="element-805">
              <span className="text-3xl" data-id="element-806">{productsData.preferred.emoji}</span>
              <span className="text-lg font-semibold text-navy" data-id="element-807">
                {t.fornitore.cleaningInProgress.preferredProducts}
              </span>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-808">
              {productsData.preferred.items.map((item, index) => <span key={index} className="inline-block px-4 py-2 bg-green-50 text-green-700 rounded-full text-base font-medium border border-green-200" data-id="element-809">
                  {item}
                </span>)}
            </div>
          </Card>

          {/* Prodotti da Evitare - Top Right */}
          <Card className="p-8" data-id="element-810">
            <div className="flex items-center gap-3 mb-5" data-id="element-811">
              <span className="text-3xl" data-id="element-812">{productsData.avoid.emoji}</span>
              <span className="text-lg font-semibold text-navy" data-id="element-813">
                {t.fornitore.cleaningInProgress.avoidProducts}
              </span>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-814">
              {productsData.avoid.items.map((item, index) => <span key={index} className="inline-block px-4 py-2 bg-red-50 text-red-700 rounded-full text-base font-medium border border-red-200" data-id="element-815">
                  {item}
                </span>)}
            </div>
          </Card>

          {/* Materiali Delicati - Bottom Left */}
          <Card className="p-8" data-id="element-816">
            <div className="flex items-center gap-3 mb-5" data-id="element-817">
              <span className="text-3xl" data-id="element-818">
                {specialAndDelicate.delicate.emoji}
              </span>
              <span className="text-lg font-semibold text-navy" data-id="element-819">
                {t.fornitore.cleaningInProgress.delicateMaterials}
              </span>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-820">
              {specialAndDelicate.delicate.items.map((item, index) => <span key={index} className="inline-block px-4 py-2 bg-yellow-50 text-yellow-700 rounded-full text-base font-medium border border-yellow-200" data-id="element-821">
                  {item}
                </span>)}
            </div>
          </Card>

          {/* Preferenze Speciali - Bottom Right */}
          <Card className="p-8" data-id="element-822">
            <div className="flex items-center gap-3 mb-5" data-id="element-823">
              <span className="text-3xl" data-id="element-824">
                {specialAndDelicate.special.emoji}
              </span>
              <span className="text-lg font-semibold text-navy" data-id="element-825">
                {t.fornitore.cleaningInProgress.specialPreferences}
              </span>
            </div>
            <ul className="space-y-3" data-id="element-826">
              {specialAndDelicate.special.items.map((item, index) => <li key={index} className="flex items-start gap-2 text-base text-gray-700 leading-relaxed" data-id="element-827">
                  <span className="text-teal-deep mt-1" data-id="element-828">•</span>
                  <span data-id="element-829">{item}</span>
                </li>)}
            </ul>
          </Card>

          {/* Note Aggiuntive - Full Width Bottom */}
          <Card className="p-8 md:col-span-2" data-id="element-830">
            <div className="flex items-start gap-4" data-id="element-831">
              <span className="text-3xl" data-id="element-832">{notes.emoji}</span>
              <div className="flex-1" data-id="element-833">
                <span className="text-lg font-semibold text-navy block mb-3" data-id="element-834">
                  {t.fornitore.cleaningInProgress.additionalNotes}
                </span>
                <p className="text-base text-gray-700 leading-relaxed" data-id="element-835">
                  {notes.text}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:p-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-40" data-id="element-836">
        <div className="max-w-4xl mx-auto" data-id="element-837">
          <Button onClick={() => navigate('/fornitore/before-photos')} className="w-full text-lg" data-id="element-838">
            <Camera className="w-5 h-5" data-id="element-839" />
            <span data-id="element-840">Inizia Intervento</span>
          </Button>
        </div>
      </div>
    </div>;
}